import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuthContext } from '@/lib/auth-context'
import { getLockerRoomBudgetState, saveLockerRoomBudget } from '@/lib/coreApi'
import { supabase } from '@/supabase/client'

type LockerBudgetAthleteRow = {
  prospectId: number
  name: string
  status: 'nominated' | 'pool' | 'matched' | 'active' | 'alumni' | string
  remainingCents: number
  annualLimitCents: number
  plannedAnnualCents: number
  plannedMonthlyCents: number
  lastSpendAt?: string | null
}

type LockerBudgetState = {
  lockerRoomId: string
  totalPotCents: number
  totalPlannedAnnualCents: number
  rows: LockerBudgetAthleteRow[]
  isLoading: boolean
  isSaving: boolean
  saveError?: string
}

const INITIAL_STATE: LockerBudgetState = {
  lockerRoomId: '',
  totalPotCents: 0,
  totalPlannedAnnualCents: 0,
  rows: [],
  isLoading: true,
  isSaving: false,
}

function formatCurrency(cents?: number | null, minimumFractionDigits = 0) {
  if (!Number.isFinite(cents)) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits,
    maximumFractionDigits: minimumFractionDigits,
  }).format((cents || 0) / 100)
}

function parseCurrencyInput(value: string) {
  const numeric = value.replace(/[^\d.]/g, '')
  const amount = Number.parseFloat(numeric)
  if (!Number.isFinite(amount)) return 0
  return Math.round(amount * 100)
}

export default function LockerRoomBudget() {
  const { user, profile } = useAuthContext() as any
  const [searchParams] = useSearchParams()
  const role = profile?.role
  const canLoadBudget = role === 'coach' || role === 'admin' || role === 'superadmin'
  const isCoach = role === 'coach'
  const [state, setState] = useState<LockerBudgetState>(INITIAL_STATE)
  const [loadError, setLoadError] = useState('')
  const [lockerRoomId, setLockerRoomId] = useState('')

  const loadBudgetState = useCallback(
    async (roomId: string) => {
      setState((prev) => ({ ...prev, isLoading: true, saveError: undefined }))
      setLoadError('')

      try {
        const result = await getLockerRoomBudgetState(roomId)
        const payload = (result?.data || {}) as LockerBudgetState
        setState({ ...payload, isSaving: false, isLoading: false })
      } catch (error: any) {
        console.error('[LockerRoomBudget] load error', error)
        setLoadError(error?.message || 'Unable to load budget state')
        setState((prev) => ({ ...prev, isLoading: false }))
      }
    },
    [setState],
  )

  const loadLockerRoomId = useCallback(async () => {
    if (!canLoadBudget || !user?.id) {
      setLoadError('Coach or admin access required to view locker room budgets.')
      setState((prev) => ({ ...prev, isLoading: false }))
      return
    }

    const lockerRoomFromQuery =
      searchParams.get('locker_room_id') || searchParams.get('lockerRoomId')

    if (lockerRoomFromQuery) {
      setLockerRoomId(lockerRoomFromQuery)
      loadBudgetState(lockerRoomFromQuery)
      return
    }

    if (!isCoach) {
      setLoadError('Specify a locker_room_id query param to load budgets as an admin.')
      setState((prev) => ({ ...prev, isLoading: false }))
      return
    }

    setState((prev) => ({ ...prev, isLoading: true }))

    const { data, error } = await supabase
      .from('locker_rooms')
      .select('id')
      .eq('coach_user_id', user.id)
      .is('archived_at', null)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    if (error) {
      setLoadError(error.message || 'Unable to load locker room')
      setState((prev) => ({ ...prev, isLoading: false }))
      return
    }

    const lockerData = data as { id: string } | null
    if (!lockerData?.id) {
      setLoadError('No locker room found for this coach.')
      setState((prev) => ({ ...prev, isLoading: false }))
      return
    }

    setLockerRoomId(lockerData.id)
    loadBudgetState(lockerData.id)
  }, [canLoadBudget, isCoach, loadBudgetState, searchParams, user?.id])

  useEffect(() => {
    loadLockerRoomId()
  }, [loadLockerRoomId])

  const isOverAllocated = state.totalPlannedAnnualCents > state.totalPotCents
  const allocationDiff = state.totalPlannedAnnualCents - state.totalPotCents

  const rowsWithFlags = useMemo(
    () =>
      state.rows.map((row) => ({
        ...row,
        isRowOverAnnual: row.plannedAnnualCents > row.annualLimitCents && row.annualLimitCents > 0,
      })),
    [state.rows],
  )

  const updatePlan = useCallback((prospectId: number, field: 'plannedAnnualCents' | 'plannedMonthlyCents', value: number) => {
    setState((prev) => {
      const rows = prev.rows.map((row) =>
        row.prospectId === prospectId ? { ...row, [field]: Math.max(value, 0) } : row,
      )
      const totalPlannedAnnualCents = rows.reduce((sum, r) => sum + (r.plannedAnnualCents || 0), 0)
      return { ...prev, rows, totalPlannedAnnualCents }
    })
  }, [])

  const handleSave = useCallback(async () => {
    if (!lockerRoomId) return
    setState((prev) => ({ ...prev, isSaving: true, saveError: undefined }))

    try {
      await saveLockerRoomBudget({
        lockerRoomId,
        rows: state.rows.map((row) => ({
          prospectId: row.prospectId,
          plannedAnnualCents: row.plannedAnnualCents,
          plannedMonthlyCents: row.plannedMonthlyCents,
        })),
      })

      setState((prev) => ({ ...prev, isSaving: false, saveError: undefined }))
      await loadBudgetState(lockerRoomId)
    } catch (error: any) {
      console.error('[LockerRoomBudget] save error', error)
      setState((prev) => ({ ...prev, isSaving: false, saveError: error?.message || 'Unable to save' }))
    }
  }, [loadBudgetState, lockerRoomId, state.rows])

  return (
    <section className="space-y-4">
      <div className="rounded-2xl bg-white/80 p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-stone-900">Locker budget planner</h2>
            <p className="text-sm text-stone-600">
              Track the remaining pot, plan annual and monthly allocations per athlete, and spot overages early.
            </p>
          </div>
          <div className="rounded-full bg-stone-900/90 px-4 py-2 text-sm font-semibold text-white">
            {formatCurrency(state.totalPotCents, 0)} pot
          </div>
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-4">
          <div className="rounded-xl bg-stone-50 p-4">
            <p className="text-xs uppercase tracking-wide text-stone-500">Total pot</p>
            <p className="text-lg font-semibold text-stone-900">{formatCurrency(state.totalPotCents)}</p>
          </div>
          <div className="rounded-xl bg-stone-50 p-4">
            <p className="text-xs uppercase tracking-wide text-stone-500">Planned (annual)</p>
            <p className="text-lg font-semibold text-stone-900">{formatCurrency(state.totalPlannedAnnualCents)}</p>
          </div>
          <div className="rounded-xl bg-stone-50 p-4">
            <p className="text-xs uppercase tracking-wide text-stone-500">Allocation delta</p>
            <p
              className={`text-lg font-semibold ${isOverAllocated ? 'text-amber-700' : 'text-stone-900'}`}
            >
              {isOverAllocated ? '+' : ''}
              {formatCurrency(allocationDiff)}
            </p>
          </div>
          <div className="rounded-xl bg-stone-50 p-4">
            <p className="text-xs uppercase tracking-wide text-stone-500">Athletes</p>
            <p className="text-lg font-semibold text-stone-900">{state.rows.length}</p>
          </div>
        </div>

        {isOverAllocated ? (
          <div className="mt-3 rounded-xl bg-amber-50 p-3 text-sm text-amber-800">
            You’ve planned {formatCurrency(allocationDiff)} more than the current pot. That’s okay for planning, but double-check before spending.
          </div>
        ) : null}

        {loadError ? (
          <p className="mt-3 text-sm text-brick">{loadError}</p>
        ) : state.isLoading ? (
          <p className="mt-3 text-sm text-stone-600">Loading budget…</p>
        ) : null}
      </div>

      <div className="rounded-2xl bg-white/80 p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-stone-900">Allocations by athlete</h3>
            <p className="text-sm text-stone-600">Edit planned budgets and monthly pacing per athlete.</p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="rounded-full bg-stone-100 px-3 py-2 text-sm font-semibold text-stone-800 hover:bg-stone-200"
              onClick={() => lockerRoomId && loadBudgetState(lockerRoomId)}
              disabled={state.isLoading}
            >
              Refresh
            </button>
            <button
              type="button"
              className="rounded-full bg-stone-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              onClick={handleSave}
              disabled={state.isSaving || state.isLoading || !state.rows.length}
            >
              {state.isSaving ? 'Saving…' : 'Save allocations'}
            </button>
          </div>
        </div>

        {state.saveError ? <p className="mt-3 text-sm text-brick">{state.saveError}</p> : null}

        {state.isLoading ? (
          <p className="mt-3 text-sm text-stone-600">Loading athletes…</p>
        ) : !state.rows.length ? (
          <p className="mt-3 text-sm text-stone-600">No athletes found for this locker room.</p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full divide-y divide-stone-200 text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-stone-500">
                  <th className="px-3 py-2">Athlete</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2 text-right">Remaining</th>
                  <th className="px-3 py-2 text-right">Annual limit</th>
                  <th className="px-3 py-2 text-right">Planned (annual)</th>
                  <th className="px-3 py-2 text-right">Planned (monthly)</th>
                  <th className="px-3 py-2">Flag</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-200">
                {rowsWithFlags.map((row) => (
                  <tr key={row.prospectId}>
                    <td className="px-3 py-3 font-semibold text-stone-900">{row.name}</td>
                    <td className="px-3 py-3 capitalize text-stone-700">{row.status || 'active'}</td>
                    <td className="px-3 py-3 text-right text-stone-900">{formatCurrency(row.remainingCents)}</td>
                    <td className="px-3 py-3 text-right text-stone-900">
                      {row.annualLimitCents > 0 ? formatCurrency(row.annualLimitCents) : '—'}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <input
                        type="number"
                        inputMode="decimal"
                        className={`w-full rounded-lg border px-2 py-2 text-right ${
                          row.isRowOverAnnual ? 'border-amber-400 bg-amber-50' : 'border-stone-200'
                        }`}
                        value={(row.plannedAnnualCents / 100).toFixed(2)}
                        onChange={(event) =>
                          updatePlan(row.prospectId, 'plannedAnnualCents', parseCurrencyInput(event.target.value))
                        }
                      />
                    </td>
                    <td className="px-3 py-3 text-right">
                      <input
                        type="number"
                        inputMode="decimal"
                        className="w-full rounded-lg border border-stone-200 px-2 py-2 text-right"
                        value={(row.plannedMonthlyCents / 100).toFixed(2)}
                        onChange={(event) =>
                          updatePlan(row.prospectId, 'plannedMonthlyCents', parseCurrencyInput(event.target.value))
                        }
                      />
                    </td>
                    <td className="px-3 py-3 text-sm text-stone-700">
                      {row.isRowOverAnnual
                        ? 'Planned exceeds annual limit'
                        : row.annualLimitCents > 0
                        ? 'Within limit'
                        : 'No limit set'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  )
}
